// The Common Trade Panel's price / protection scale: ONE component, one mode per strategy, drawn only from figures
// the row already carries (never estimated). A strategy without the needed figures shows a neutral "—".
//   BAND     SELL options: protected band (green) inside the expiry break-evens, spot marker
//   STRIKES  BUY options: the two strikes, spot marker
//   CONDOR   iron condor: wings K1..K4, profit zone between the shorts, break-evens, spot marker
//   BOX      box spread: K1 / K2 only - its expiry P&L is fixed by the strikes, not by the spot
//   SPREAD   futures spread: entry spread, spread now, next trigger (a spread scale, not a price scale)
import React from "react";

const fmt = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 }));

function Track({ lo, hi, zones = [], marks = [], spot, spotLabel = "Spot", testId, caption, outside, edges }) {
  if (lo == null || hi == null || !(hi > lo)) return <span className="text-[var(--term-muted)]">—</span>;
  const pad = (hi - lo) * 0.08;
  const a = lo - pad, b = hi + pad;
  const pos = (v) => `${Math.max(0, Math.min(100, ((v - a) / (b - a)) * 100))}%`;
  const out = outside ?? (spot != null && (spot < lo || spot > hi));
  // a spot beyond the drawn range is pinned to the edge: say by how much, never let the pin read as "at the edge"
  const beyond = spot != null && (spot < a || spot > b) ? ` ${spot < a ? "◀" : "▶"} ${(Math.abs(spot - (spot < a ? lo : hi)) / Math.abs(spot) * 100).toFixed(2)}% beyond` : "";
  return (
    <div className="w-[140px] min-w-[130px]" data-testid={testId}>
      <div className="text-[10px] text-center mono leading-tight h-3.5">{spot != null ? <>{spotLabel} <b>{fmt(spot)}</b>{beyond && <span className="text-[#FF3B30]">{beyond}</span>}</> : caption || ""}</div>
      <div className="relative h-2.5 rounded-full bg-[#FF3B30]/25 my-0.5">
        {zones.map((z, i) => <div key={i} className={`absolute top-0 h-full ${z.cls}`} style={{ left: pos(z.from), width: `calc(${pos(z.to)} - ${pos(z.from)})` }} />)}
        {marks.map((m, i) => <div key={i} className="absolute top-[-2px] h-[14px] w-px bg-[var(--term-primary)]" style={{ left: pos(m) }} />)}
        {spot != null && <div className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-3 w-3 rounded-full border-2 border-white ${out ? "bg-[#FF3B30]" : "bg-[#00C805]"}`}
          style={{ left: pos(spot) }} data-testid={testId ? `${testId}-spot` : undefined} />}
      </div>
      <div className="flex justify-between text-[10px] mono text-[var(--term-muted)] leading-tight"><span>{edges ? edges[0] : fmt(lo)}</span><span>{edges ? edges[1] : fmt(hi)}</span></div>
    </div>
  );
}

export default function ProtectionScale({ row }) {
  const s = row.scale;
  const tid = `scale-${row.key}`;
  if (!s) return <span className="text-[var(--term-muted)]" title="Not available for this trade yet">—</span>;
  if (s.kind === "BAND") {
    const one = (s.lower_be == null) !== (s.upper_be == null);
    if (one) {
      // ONE break-even (a rescue protected the other side): profit runs to infinity beyond it - never pair it with a band edge
      const be = s.lower_be ?? s.upper_be, up = s.lower_be != null;   // up: profit ABOVE the break-even
      const pts = [be, row.spot, s.band_lower, s.band_upper].filter((x) => x != null);
      const span = Math.max(...pts) - Math.min(...pts) || Math.abs(be) * 0.02;
      const lo = Math.min(...pts) - (up ? span * 0.3 : 0), hi = Math.max(...pts) + (up ? 0 : span * 0.3);
      return <Track testId={tid} lo={lo} hi={hi} spot={row.spot} outside={row.spot != null && (up ? row.spot < be : row.spot > be)}
        caption={up ? "profit above BE →" : "← profit below BE"}
        zones={[{ from: up ? be : lo, to: up ? hi : be, cls: "bg-[#00C805]/25" }, ...(s.band_lower != null ? [{ from: s.band_lower, to: s.band_upper, cls: "bg-[#00C805]/45" }] : [])]}
        marks={[be]} edges={up ? [`BE ${fmt(be)}`, "∞"] : ["0", `BE ${fmt(be)}`]} />;
    }
    const lo = s.lower_be ?? s.band_lower, hi = s.upper_be ?? s.band_upper;
    return <Track testId={tid} lo={lo} hi={hi} spot={row.spot}
      zones={[{ from: lo, to: hi, cls: "bg-[#00C805]/25" }, ...(s.band_lower != null ? [{ from: s.band_lower, to: s.band_upper, cls: "bg-[#00C805]/45" }] : [])]}
      marks={[s.band_lower, s.band_upper].filter((x) => x != null)} />;
  }
  if (s.kind === "STRIKES") {
    return <Track testId={tid} lo={s.low} hi={s.high} spot={row.spot} zones={[{ from: s.low, to: s.high, cls: "bg-[#2563EB]/20" }]} marks={[s.low, s.high]} />;
  }
  if (s.kind === "CONDOR") {
    const [k1, k2, k3, k4] = s.strikes || [];
    return <Track testId={tid} lo={k1} hi={k4} spot={row.spot}
      zones={[{ from: s.lower_be ?? k2, to: s.upper_be ?? k3, cls: "bg-[#00C805]/25" }, { from: k2, to: k3, cls: "bg-[#00C805]/45" }]} marks={[k2, k3]} />;
  }
  if (s.kind === "BOX") {
    const [k1, k2] = s.strikes || [];
    return <Track testId={tid} lo={k1} hi={k2} zones={[{ from: k1, to: k2, cls: "bg-[#7c3aed]/25" }]} caption="P&L fixed at expiry" />;
  }
  if (s.kind === "SPREAD") {
    const vals = [s.entry, s.now, s.next].filter((x) => x != null);
    const lo = Math.min(...vals, 0), hi = Math.max(...vals);
    return <Track testId={tid} lo={lo} hi={hi > lo ? hi : lo + 1} spot={s.now} spotLabel="Spread"
      zones={s.entry != null ? [{ from: lo, to: s.entry, cls: "bg-[#00C805]/30" }] : []} marks={[s.entry, s.next].filter((x) => x != null)} />;
  }
  return <span className="text-[var(--term-muted)]">—</span>;
}
